// S-expression parser for the dashboard engine.
// Single entry point `parse(src)` returns one `AstNode` (the grammar's
// `Program = Expression` rule). All syntactic checks are parse-time:
// list-head-must-be-a-symbol, positional-before-kwarg, no bare keywords,
// reserved words, and so on. Errors are thrown as `ParseError` carrying one
// `Diagnostic`.
//
// Design notes:
// - Immutable lex state (`Lex`) — every consumed character produces a new
//   state. Line/col/offset are tracked together so every token's `SourcePos`
//   is exact.
// - Word boundaries split tokens by class: whitespace, delimiters `()[]`,
//   string opener `"`, comment opener `;`, and keyword opener `:`.
// - Number vs symbol disambiguation follows a "lookahead" rule: a
//   word that starts with a digit, or with `-` followed by a digit, is
//   classified as a number and validated against `NUMBER_RE`; otherwise it
//   is classified as a symbol and validated against `SYMBOL_RE`. A word that
//   fails its expected class is a diagnostic, not a silently-accepted
//   symbol — so `1abc`, `1-2`, and `sales.revenue` all surface clear errors.

import type { AstArg, AstNode, KeywordNode, ListNode, MapNode, SourcePos, VectorNode } from './ast.ts';
import { NO_POS } from './ast.ts';
import {
  EmptyKeywordError,
  EmptyListError,
  HashFnArgGapError,
  HashFnEmptyError,
  HashFnMixedArgError,
  InvalidNumberError,
  InvalidTokenError,
  ListHeadNotSymbolError,
  MapKeyNotKeywordError,
  MapMissingValueError,
  NestedHashFnError,
  ReservedWordError,
  StrayRbraceError,
  StrayRbracketError,
  StrayRparenError,
  UnclosedListError,
  UnclosedMapError,
  UnclosedVectorError,
  UnexpectedEofError,
  UnknownEscapeError,
  UnterminatedStringError,
} from './errors.ts';

// Lexical tokens. Exposed alongside `tokenize` so the source formatter
// (`./format.ts`) can re-lay-out an expression by slicing surface text
// from each token's `pos` — no second lexer, no AST round-trip.
export type Tok =
  | {
      readonly t: 'lparen' | 'rparen' | 'lbracket' | 'rbracket' | 'lbrace' | 'rbrace' | 'hash-lparen';
      readonly pos: SourcePos;
    }
  | { readonly t: 'number'; readonly value: number; readonly pos: SourcePos }
  | { readonly t: 'string'; readonly value: string; readonly pos: SourcePos }
  | { readonly t: 'boolean'; readonly value: boolean; readonly pos: SourcePos }
  | { readonly t: 'null'; readonly pos: SourcePos }
  | { readonly t: 'symbol'; readonly name: string; readonly pos: SourcePos }
  | { readonly t: 'keyword'; readonly name: string; readonly pos: SourcePos };

// Character predicates and grammar regexes

const isWs = (c: string | undefined): boolean => c === ' ' || c === '\t' || c === '\n' || c === '\r';

const isDelim = (c: string | undefined): boolean =>
  c === '(' || c === ')' || c === '[' || c === ']' || c === '{' || c === '}';

const isDigit = (c: string | undefined): boolean => c !== undefined && c >= '0' && c <= '9';

/**
 * `SymbolStart` and `SymbolCont` from the grammar EBNF. A narrower
 * SymbolCont (no operator chars) is possible; we follow the broader EBNF,
 * which operator-style names (`>=`, `&&`-style) require. `$` is admitted as
 * an extension so it can appear within identifiers (e.g., the
 * symbol-continuation form `col$id`) — the producer and the surface
 * grammar must agree on the identifier alphabet. `%` is admitted so the
 * `#(…)` reader macro's implicit parameters (`%`, `%1`, `%2`, …) tokenize
 * as symbols inside the body.
 */
const SYMBOL_START_CHARS = '_$+-*/<>=!?&|%';
const isSymbolStart = (c: string): boolean =>
  (c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z') || SYMBOL_START_CHARS.includes(c);
const isSymbolCont = (c: string): boolean => isSymbolStart(c) || isDigit(c);

/**
 * Keywords admit `.` in continuation chars (e.g., `:metric.average`,
 * `:my-ns.sub/kw`) so dotted identifier names map to a keyword without
 * forcing a `(col …)` rewrite. Symbol alphabet stays narrower — dotted
 * symbols like `sales.revenue` remain rejected so the parser can suggest
 * `(col …)` as the fix (see `InvalidTokenError` diagnostic).
 */
const isKeywordCont = (c: string): boolean => isSymbolCont(c) || c === '.';

// A word boundary terminates a SYMBOL/NUMBER/BOOLEAN/NULL token. Everything
// else (letters, digits, operator chars, `_`, `-`, `.`, `/`, etc.) is read
// into the word and then classified post-scan.
const isWordBoundary = (c: string | undefined): boolean =>
  c === undefined || isWs(c) || isDelim(c) || c === '"' || c === ';' || c === ':' || c === '#';

/**
 * NUMBER per the grammar EBNF: optional leading `-`, then digits with optional `_`
 * separators, optional fractional part (digits + `_`), optional exponent.
 */
const NUMBER_RE = /^-?\d[\d_]*(?:\.\d[\d_]*)?(?:[eE][+-]?\d+)?$/;

/**
 * SYMBOL per the grammar EBNF: SymbolStart followed by zero or more SymbolCont.
 * Must stay in sync with `SYMBOL_START_CHARS` above (`%` for `#(…)` params,
 * `$` for engine-synthesised columns).
 */
const SYMBOL_RE = /^[A-Za-z_$+\-*/<>=!?&|%][A-Za-z0-9_$+\-*/<>=!?&|%]*$/;

// `\uXXXX` escapes in strings: exactly four hex digits.
const UNICODE_HEX_RE = /^[0-9A-Fa-f]{4}$/;

const RESERVED_WORDS: ReadonlySet<string> = new Set(['true', 'false', 'null']);

const STRING_ESCAPES: Readonly<Record<string, string>> = {
  n: '\n',
  t: '\t',
  r: '\r',
  '"': '"',
  '\\': '\\',
};

// Single source of truth for paren-like tokens: kind → recognising char.
// Both the lexer's char→kind table (`SINGLE_CHAR_TOKS`) and the
// diagnostics-facing describers (`TOK_DESCRIBERS`) derive from this; adding
// a new paren-like kind needs an update only here (plus the `Tok` union).
const PAREN_CHARS = {
  lparen: '(',
  rparen: ')',
  lbracket: '[',
  rbracket: ']',
  lbrace: '{',
  rbrace: '}',
} as const satisfies Partial<Record<Tok['t'], string>>;

// Per-token contribution to the `)` − `(` balance (`#(` opens like `(`).
// Paren-specific by design: bracket/brace kinds contribute 0, so the
// count isolates the parenthesis imbalance behind `unmatched closing )`.
const PAREN_DELTA: Partial<Record<Tok['t'], number>> = { lparen: -1, 'hash-lparen': -1, rparen: 1 };

const SINGLE_CHAR_TOKS: Readonly<Record<string, Tok['t']>> = Object.fromEntries(
  Object.entries(PAREN_CHARS).map(([kind, char]) => [char, kind as Tok['t']] as const),
);

// Lexer

type Lex = {
  readonly src: string;
  readonly i: number; //    0-based byte offset
  readonly line: number; // 1-based line number
  readonly col: number; //  1-based column number
};

const initLex = (src: string): Lex => ({ src, i: 0, line: 1, col: 1 });

// Advance `n` characters; recompute line/col by inspecting consumed text.
const advance = (lex: Lex, n: number): Lex => {
  if (n <= 0) {
    return lex;
  }
  const consumed = lex.src.slice(lex.i, lex.i + n);
  const newlines = (consumed.match(/\n/g) ?? []).length;
  const lastNl = consumed.lastIndexOf('\n');
  return {
    src: lex.src,
    i: lex.i + n,
    line: lex.line + newlines,
    col: lastNl === -1 ? lex.col + n : n - lastNl,
  };
};

const posAt = (lex: Lex, length: number): SourcePos => ({
  line: lex.line,
  col: lex.col,
  offset: lex.i,
  length,
});

const posFrom = (start: Lex, end: Lex): SourcePos => ({
  line: start.line,
  col: start.col,
  offset: start.i,
  length: end.i - start.i,
});

type Read = { readonly tok: Tok | null; readonly next: Lex };

const skipComment = (lex: Lex): Lex => {
  const nl = lex.src.indexOf('\n', lex.i);
  const end = nl === -1 ? lex.src.length : nl;
  return advance(lex, end - lex.i);
};

const readString = (start: Lex): Read => {
  // `start` points at the opening `"`.
  const consume = (cur: Lex, value: string): Read => {
    const c = cur.src[cur.i];
    if (c === undefined) {
      throw new UnterminatedStringError(posFrom(start, cur));
    }
    if (c === '"') {
      const next = advance(cur, 1);
      return { tok: { t: 'string', value, pos: posFrom(start, next) }, next };
    }
    if (c === '\\') {
      const esc = cur.src[cur.i + 1];
      if (esc === undefined) {
        throw new UnterminatedStringError(posFrom(start, cur));
      }
      if (esc === 'u') {
        const hex = cur.src.slice(cur.i + 2, cur.i + 6);
        if (!UNICODE_HEX_RE.test(hex)) {
          throw new UnknownEscapeError(`u${hex}`, posAt(cur, Math.min(6, cur.src.length - cur.i)));
        }
        return consume(advance(cur, 6), value + String.fromCharCode(Number.parseInt(hex, 16)));
      }
      const decoded = STRING_ESCAPES[esc];
      if (decoded === undefined) {
        throw new UnknownEscapeError(esc, posAt(cur, 2));
      }
      return consume(advance(cur, 2), value + decoded);
    }
    return consume(advance(cur, 1), value + c);
  };
  return consume(advance(start, 1), '');
};

const readKeyword = (start: Lex): Read => {
  // `start` points at `:`.
  const afterColon = advance(start, 1);
  const first = afterColon.src[afterColon.i];
  if (first === undefined || !isSymbolStart(first)) {
    throw new EmptyKeywordError(posFrom(start, afterColon));
  }
  const scan = (cur: Lex): Lex => {
    const c = cur.src[cur.i];
    if (c === undefined || !isKeywordCont(c)) {
      return cur;
    }
    return scan(advance(cur, 1));
  };
  const end = scan(afterColon);
  const name = afterColon.src.slice(afterColon.i, end.i);
  if (RESERVED_WORDS.has(name)) {
    throw new ReservedWordError(name, posFrom(start, end));
  }
  return { tok: { t: 'keyword', name, pos: posFrom(start, end) }, next: end };
};

const readWord = (start: Lex): Read => {
  const scan = (cur: Lex): Lex => {
    const c = cur.src[cur.i];
    if (isWordBoundary(c)) {
      return cur;
    }
    return scan(advance(cur, 1));
  };
  const end = scan(start);
  const word = start.src.slice(start.i, end.i);
  const pos = posFrom(start, end);

  // Reserved words are lexed as dedicated tokens — they cannot be used as
  // symbols, op names, or column names.
  if (word === 'true' || word === 'false') {
    return { tok: { t: 'boolean', value: word === 'true', pos }, next: end };
  }
  if (word === 'null') {
    return { tok: { t: 'null', pos }, next: end };
  }

  // Number/symbol lookahead: a `-` immediately followed by a digit starts a number;
  // bare `-` (and `-` followed by non-digit) is a symbol. Equivalent
  // post-scan rule: if the word begins with a digit or with `-<digit>`, it
  // is number-shaped and validated as a number; otherwise it is
  // symbol-shaped.
  const c0 = word[0];
  const looksNumeric = isDigit(c0) || (c0 === '-' && word.length >= 2 && isDigit(word[1]));

  if (looksNumeric) {
    if (!NUMBER_RE.test(word)) {
      throw new InvalidNumberError(word, pos);
    }
    return { tok: { t: 'number', value: Number(word.replace(/_/g, '')), pos }, next: end };
  }

  if (!SYMBOL_RE.test(word)) {
    throw new InvalidTokenError(word, pos);
  }

  return { tok: { t: 'symbol', name: word, pos }, next: end };
};

const readToken = (lex: Lex): Read => {
  const c = lex.src[lex.i];
  if (c === undefined) {
    return { tok: null, next: lex };
  }
  if (isWs(c)) {
    return { tok: null, next: advance(lex, 1) };
  }
  if (c === ';') {
    return { tok: null, next: skipComment(lex) };
  }
  const single = SINGLE_CHAR_TOKS[c];
  if (single !== undefined) {
    const next = advance(lex, 1);
    // biome-ignore lint/suspicious/noExplicitAny: FIXME — token-kind union narrowing through SINGLE_CHAR_TOKS lookup defeats TS discriminant inference
    return { tok: { t: single, pos: posFrom(lex, next) }, next } as any;
  }
  if (c === '"') {
    return readString(lex);
  }
  if (c === ':') {
    return readKeyword(lex);
  }
  if (c === '#') {
    const next2 = lex.src[lex.i + 1];
    if (next2 !== '(') {
      throw new InvalidTokenError('#', posAt(lex, 1));
    }
    const next = advance(lex, 2);
    return { tok: { t: 'hash-lparen', pos: posFrom(lex, next) }, next };
  }
  return readWord(lex);
};

export type TokenizeResult = { readonly tokens: readonly Tok[]; readonly eof: SourcePos };

export const tokenize = (src: string): TokenizeResult => {
  const go = (lex: Lex, acc: readonly Tok[]): { tokens: readonly Tok[]; finalLex: Lex } => {
    if (lex.i >= lex.src.length) {
      return { tokens: acc, finalLex: lex };
    }
    const { tok, next } = readToken(lex);
    return go(next, tok ? [...acc, tok] : acc);
  };
  const { tokens, finalLex } = go(initLex(src), []);
  return {
    tokens,
    eof: { line: finalLex.line, col: finalLex.col, offset: finalLex.i, length: 0 },
  };
};

// Parser (recursive descent over the token stream)

type Cur = { readonly toks: readonly Tok[]; readonly p: number };
type Parsed<T> = { readonly node: T; readonly cur: Cur };

const peek = (cur: Cur): Tok | undefined => cur.toks[cur.p];
const step = (cur: Cur): Cur => ({ toks: cur.toks, p: cur.p + 1 });

// Paren-like describers are derived from `PAREN_CHARS` so the recognising
// char and the diagnostic text don't drift. The mapped-type cast restores
// the discriminant-narrowed shape that `Object.fromEntries` erases.
const PAREN_DESCRIBERS = Object.fromEntries(
  Object.entries(PAREN_CHARS).map(([kind, char]) => [kind, () => `"${char}"`] as const),
) as { [KeyType in keyof typeof PAREN_CHARS]: () => string };

// Lookup table: token kind → describer. The mapped type preserves each
// branch's discriminant narrowing so `tok.value` / `tok.name` access is
// type-checked without manual casts inside each describer.
const TOK_DESCRIBERS: { [KeyType in Tok['t']]: (tok: Extract<Tok, { t: KeyType }>) => string } = {
  ...PAREN_DESCRIBERS,
  'hash-lparen': () => '"#("',
  string: () => 'string literal',
  null: () => 'reserved word "null"',
  number: (tok) => `number ${tok.value}`,
  boolean: (tok) => `reserved word "${String(tok.value)}"`,
  symbol: (tok) => `symbol "${tok.name}"`,
  keyword: (tok) => `keyword ":${tok.name}"`,
};

const describeTok = (tok: Tok): string => (TOK_DESCRIBERS[tok.t] as (tok: Tok) => string)(tok);

// Closing delim spans the closing token; the list/vector span runs from the
// open delim's offset to one past the close delim.
const spanFromTo = (open: SourcePos, close: SourcePos): SourcePos => ({
  line: open.line,
  col: open.col,
  offset: open.offset,
  length: close.offset + close.length - open.offset,
});
/**
 * Collect every `%`-style implicit parameter referenced in a `#(…)` body.
 * Walks the parsed body purely (no mutation, no traversal side effects),
 * returning the unique set of names in encounter order. `parseHashFn`
 * passes the result to `deriveHashFnParams`, which validates the set and
 * yields the synthetic parameter list.
 */
const collectHashFnParamRefs = (node: AstNode): readonly string[] => {
  if (node.kind === 'symbol') {
    return isHashFnParam(node.name) ? [node.name] : [];
  }
  if (node.kind === 'list') {
    return node.args.flatMap((a) => collectHashFnParamRefs(a.value));
  }
  if (node.kind === 'vector') {
    return node.elements.flatMap(collectHashFnParamRefs);
  }
  if (node.kind === 'map') {
    return node.entries.flatMap((e) => collectHashFnParamRefs(e.value));
  }
  return [];
};

const HASH_FN_NUMBERED_RE = /^%([1-9]\d*)$/;
const isHashFnParam = (name: string): boolean => name === '%' || HASH_FN_NUMBERED_RE.test(name);

/**
 * Resolve the synthetic parameter list for a `#(…)` reader macro from
 * the set of `%`-style symbols its body references. Three rules:
 *  - bare `%` alone → params `['%']` (single-arg shorthand);
 *  - `%1 %2 … %N` contiguous from 1 → params `['%1' … '%N']`;
 *  - no `%` references at all → params `[]` (zero-arg closure).
 * Mixing bare `%` with numbered forms, or skipping a numbered index,
 * is rejected with a dedicated diagnostic.
 */
const deriveHashFnParams = (body: AstNode, openPos: SourcePos): readonly string[] => {
  const refs = new Set(collectHashFnParamRefs(body));
  if (refs.size === 0) {
    return [];
  }
  const hasBare = refs.has('%');
  const numbered = [...refs]
    .map((n) => HASH_FN_NUMBERED_RE.exec(n)?.[1])
    .filter((s): s is string => s !== undefined)
    .map((s) => Number.parseInt(s, 10))
    .sort((a, b) => a - b);
  if (hasBare && numbered.length > 0) {
    throw new HashFnMixedArgError(openPos);
  }
  if (hasBare) {
    return ['%'];
  }
  const max = numbered.at(-1) as number;
  const missingIndex = Array.from({ length: max }, (_, i) => i + 1).findIndex((n) => !numbered.includes(n));
  if (missingIndex !== -1) {
    throw new HashFnArgGapError(missingIndex, openPos);
  }
  return numbered.map((n) => `%${n}`);
};

export function parse(src: string): readonly AstNode[] {
  // Defensive: a non-string (e.g. `undefined` from a tool call whose
  // args are still streaming) must fail as a structured `ParseError`,
  // not an unstructured `TypeError: lex.src is undefined` that escapes
  // the engine's error contract and tears down React subtrees. Empty /
  // whitespace-only input still flows through the `tokens.length === 0`
  // path below for an accurate `eof` position.
  if (typeof src !== 'string') {
    throw new UnexpectedEofError('expected an expression, got empty input', NO_POS);
  }
  const { tokens, eof } = tokenize(src);

  if (tokens.length === 0) {
    throw new UnexpectedEofError('expected an expression, got empty input', eof);
  }

  // Tracks `#(…)` reader-macro nesting; checked by parseHashFn to reject
  // nested reader macros (Clojure parity — nesting forces use of `fn`).
  let hashFnDepth = 0;

  // A short source window around an offset, for error context — renders
  // as `… token) …` so the agent sees where balancing actually breaks.
  const srcContext = (offset: number, radius = 24): string => {
    const start = Math.max(0, offset - radius);
    const end = Math.min(src.length, offset + radius);
    return `${start > 0 ? '… ' : ''}${src.slice(start, end)}${end < src.length ? ' …' : ''}`;
  };

  // Net `)` − `(` over the whole token stream. A positive value is the
  // exact number of surplus closing parens — fed to `StrayRparenError`
  // so the recovery hint states the concrete imbalance, not a guess.
  const parenImbalance = (): number => tokens.reduce((n, t) => n + (PAREN_DELTA[t.t] ?? 0), 0);

  // Mutual recursion via const-arrow closures: parseExpr references parseList,
  // parseVector, parseMap, and parseHashFn — all of which reference parseExpr.
  // References are resolved at call time, by which point all are defined.
  const parseList = (cur: Cur, openPos: SourcePos): Parsed<ListNode> => {
    const afterOpen = step(cur);
    const head = peek(afterOpen);
    if (head === undefined) {
      throw new UnclosedListError(openPos);
    }
    if (head.t === 'rparen') {
      throw new EmptyListError(spanFromTo(openPos, head.pos));
    }

    // List head can be either a symbol (regular op call) or a keyword.
    // A keyword head — `(:k target …)` — is sugar for polymorphic property
    // access; the desugar to `(col target :k …)` happens after the args are
    // collected so the AST stays homogeneous (every list node carries a
    // symbol `op`, not a token discriminator).
    if (head.t !== 'symbol' && head.t !== 'keyword') {
      throw new ListHeadNotSymbolError(describeTok(head), head.pos);
    }

    // Every parsed expression becomes a positional arg. Keywords are
    // emitted as positional KeywordNodes; the schema-directed lift in
    // `parseOpCall` promotes trailing `:k v` runs into kwargs when the op
    // schema declares them. The parser stays free of schema knowledge.
    const collectArgs = (
      c: Cur,
      acc: readonly AstArg[],
    ): { args: readonly AstArg[]; afterClose: Cur; closePos: SourcePos } => {
      const t = peek(c);
      if (t === undefined) {
        throw new UnclosedListError(openPos);
      }
      if (t.t === 'rparen') {
        return { args: acc, afterClose: step(c), closePos: t.pos };
      }
      const { node, cur: afterValue } = parseExpr(c);
      return collectArgs(afterValue, [...acc, { kind: 'positional', value: node }]);
    };

    const afterHead = step(afterOpen);
    const { args, afterClose, closePos } = collectArgs(afterHead, []);

    if (head.t === 'keyword') {
      // Keyword-head desugar: `(:k target rest…)` → `(col target :k rest…)`.
      const target = args[0];
      if (target === undefined) {
        throw new EmptyListError(spanFromTo(openPos, closePos));
      }
      const rest = args.slice(1);
      const keyArg: AstArg = {
        kind: 'positional',
        value: { kind: 'keyword', name: head.name, pos: head.pos },
      };
      const desugared: readonly AstArg[] = [{ kind: 'positional', value: target.value }, keyArg, ...rest];
      return {
        node: {
          kind: 'list',
          op: 'col',
          opPos: head.pos,
          args: desugared,
          pos: spanFromTo(openPos, closePos),
        },
        cur: afterClose,
      };
    }

    return {
      node: {
        kind: 'list',
        op: head.name,
        opPos: head.pos,
        args,
        pos: spanFromTo(openPos, closePos),
      },
      cur: afterClose,
    };
  };

  const parseVector = (cur: Cur, openPos: SourcePos): Parsed<VectorNode> => {
    const collectElems = (
      c: Cur,
      acc: readonly AstNode[],
    ): { elems: readonly AstNode[]; afterClose: Cur; closePos: SourcePos } => {
      const t = peek(c);
      if (t === undefined) {
        throw new UnclosedVectorError(openPos);
      }
      if (t.t === 'rbracket') {
        return { elems: acc, afterClose: step(c), closePos: t.pos };
      }
      const { node, cur: afterElem } = parseExpr(c);
      return collectElems(afterElem, [...acc, node]);
    };

    const { elems, afterClose, closePos } = collectElems(step(cur), []);
    return {
      node: { kind: 'vector', elements: elems, pos: spanFromTo(openPos, closePos) },
      cur: afterClose,
    };
  };

  const parseMap = (cur: Cur, openPos: SourcePos): Parsed<MapNode> => {
    type Entry = { readonly key: KeywordNode; readonly value: AstNode };
    const collectEntries = (
      c: Cur,
      acc: readonly Entry[],
    ): { entries: readonly Entry[]; afterClose: Cur; closePos: SourcePos } => {
      const t = peek(c);
      if (t === undefined) {
        throw new UnclosedMapError(openPos);
      }
      if (t.t === 'rbrace') {
        return { entries: acc, afterClose: step(c), closePos: t.pos };
      }
      if (t.t !== 'keyword') {
        throw new MapKeyNotKeywordError(describeTok(t), t.pos);
      }
      const key: KeywordNode = { kind: 'keyword', name: t.name, pos: t.pos };
      const afterKey = step(c);
      const valTok = peek(afterKey);
      if (valTok === undefined || valTok.t === 'rbrace') {
        throw new MapMissingValueError(t.name, t.pos);
      }
      const { node: value, cur: afterValue } = parseExpr(afterKey);
      return collectEntries(afterValue, [...acc, { key, value }]);
    };

    const { entries, afterClose, closePos } = collectEntries(step(cur), []);
    return {
      node: { kind: 'map', entries, pos: spanFromTo(openPos, closePos) },
      cur: afterClose,
    };
  };

  const parseHashFn = (cur: Cur, openPos: SourcePos): Parsed<ListNode> => {
    if (hashFnDepth > 0) {
      throw new NestedHashFnError(openPos);
    }
    hashFnDepth += 1;
    // `#(<expr>)` accepts any single expression as the body. If the first
    // token after `#(` is a symbol or keyword (list-callable head), we
    // delegate to `parseList` so `#(> (:a %) 1)` keeps its conventional
    // list-call shape. Otherwise we parse one expression and require `)`,
    // letting non-call bodies like `#({:k v})`, `#([1 2])`, or `#(42)`
    // act as the function body directly.
    const afterOpen = step(cur);
    const head = peek(afterOpen);
    if (head === undefined) {
      throw new UnclosedListError(openPos);
    }
    const inner = ((): Parsed<AstNode> => {
      if (head.t === 'rparen') {
        throw new HashFnEmptyError(spanFromTo(openPos, head.pos));
      }
      if (head.t === 'symbol' || head.t === 'keyword') {
        return parseList(cur, openPos);
      }
      const expr = parseExpr(afterOpen);
      const close = peek(expr.cur);
      if (close === undefined || close.t !== 'rparen') {
        throw new UnclosedListError(openPos);
      }
      return { node: expr.node, cur: step(expr.cur) };
    })();
    hashFnDepth -= 1;

    const params = deriveHashFnParams(inner.node, openPos);
    const paramVector: VectorNode = {
      kind: 'vector',
      elements: params.map((name) => ({ kind: 'symbol', name, pos: openPos })),
      pos: openPos,
    };
    const fnNode: ListNode = {
      kind: 'list',
      op: 'fn',
      opPos: openPos,
      args: [
        { kind: 'positional', value: paramVector },
        { kind: 'positional', value: inner.node },
      ],
      pos: inner.node.pos,
    };
    return { node: fnNode, cur: inner.cur };
  };

  const parseExpr = (cur: Cur): Parsed<AstNode> => {
    const tok = peek(cur);
    if (tok === undefined) {
      throw new UnexpectedEofError('unexpected end of input', eof);
    }
    if (tok.t === 'lparen') {
      return parseList(cur, tok.pos);
    }
    if (tok.t === 'lbracket') {
      return parseVector(cur, tok.pos);
    }
    if (tok.t === 'lbrace') {
      return parseMap(cur, tok.pos);
    }
    if (tok.t === 'hash-lparen') {
      return parseHashFn(cur, tok.pos);
    }
    if (tok.t === 'symbol') {
      return {
        node: { kind: 'symbol', name: tok.name, pos: tok.pos },
        cur: step(cur),
      };
    }
    if (tok.t === 'keyword') {
      return {
        node: { kind: 'keyword', name: tok.name, pos: tok.pos },
        cur: step(cur),
      };
    }
    if (tok.t === 'rparen') {
      throw new StrayRparenError(tok.pos, srcContext(tok.pos.offset), parenImbalance());
    }
    if (tok.t === 'rbracket') {
      throw new StrayRbracketError(tok.pos);
    }
    if (tok.t === 'rbrace') {
      throw new StrayRbraceError(tok.pos);
    }
    // Remaining cases: number, string, boolean, null — all literal nodes.
    // biome-ignore lint/suspicious/noExplicitAny: FIXME — literal tokens carry .value but TS can't narrow tok's discriminant past the rbrace/rbracket checks above
    const value = tok.t === 'null' ? null : (tok as any).value;
    return {
      node: { kind: 'literal', value, pos: tok.pos },
      cur: step(cur),
    };
  };

  // A script is one-or-more top-level expressions; the caller evaluates them
  // in order and the value of the last expression is the script's result.
  // Sequencing is a property of the entry point, not an explicit op.
  const collectForms = (cur: Cur, acc: readonly AstNode[]): readonly AstNode[] => {
    if (cur.p >= tokens.length) {
      return acc;
    }
    // A top-level `)` is an over-close. When several complete forms were
    // already parsed, an extra `)` upstream was absorbed as an early closer
    // and split one expression into pieces — blame the start of the second
    // form (adjacent to the real stray paren) rather than this trailing
    // leftover, which sits far from the mistake.
    const t = peek(cur);
    if (t !== undefined && t.t === 'rparen') {
      const second = acc.length >= 2 ? acc[1] : undefined;
      const blame = second?.pos ?? t.pos;
      // Unambiguous trailing over-close: one complete form parsed and
      // every leftover token is a surplus `)`. The corrected expression is
      // exactly that form's source — hand it back ready to resubmit.
      const trailingExcess = acc.length === 1 && tokens.slice(cur.p).every((tok) => tok.t === 'rparen');
      const balanced = trailingExcess ? src.slice(0, t.pos.offset).trimEnd() : undefined;
      throw new StrayRparenError(blame, srcContext(blame.offset), parenImbalance(), balanced);
    }
    const { node, cur: afterForm } = parseExpr(cur);
    return collectForms(afterForm, [...acc, node]);
  };

  return collectForms({ toks: tokens, p: 0 }, []);
}
